import { useMemo, useState } from "react";
import { postIpc } from "../../lib/ipc";
import { ToolSprite } from "./ToolSprite";
import { ToolsWorkbenchHeader } from "./ToolsWorkbenchHeader";
import {
  accentForGroup,
  capabilitySummary,
  fallbackPayload,
  hasGlobalInstall,
  projectEnabled,
  spriteForId,
  type OtelView,
  type CapabilityView,
  type ToolGroupView,
  type ToolListItem,
  type ToolsPayload,
} from "./model";

function visibleEntries(groups: ToolGroupView[], activeGroup: string, query: string) {
  const normalizedQuery = query.trim().toLowerCase();
  return groups
    .map((group) => ({
      ...group,
      entries: group.entries.filter((item) => hasGlobalInstall(item) || projectEnabled(item)),
    }))
    .filter((group) => group.entries.length > 0)
    .filter((group) => activeGroup === "all" || group.label === activeGroup)
    .flatMap((group) =>
      group.entries
        .filter((item) => {
          if (!normalizedQuery) return true;
          return `${item.name} ${item.summary} ${group.label}`.toLowerCase().includes(normalizedQuery);
        })
        .map((item) => ({ ...item, group: group.label })),
    );
}

function renderToolMark(item: ToolListItem & { group: string }) {
  if (item.icon_svg) {
    return (
      <ToolSprite
        accent={accentForGroup(item.group)}
        shape={spriteForId(item.id)}
        iconSvg={item.icon_svg}
        className="tool-sprite--catalog"
      />
    );
  }
  return <span className={`marketplace__swatch marketplace__swatch--${accentForGroup(item.group)}`} />;
}

function renderSelectedMark(selected: CapabilityView) {
  if (selected.icon_svg) {
    return (
      <ToolSprite
        accent={accentForGroup(selected.kind_label)}
        shape={spriteForId(selected.id)}
        iconSvg={selected.icon_svg}
        className="tool-sprite--focus"
      />
    );
  }
  return (
    <ToolSprite
      accent={accentForGroup(selected.kind_label)}
      shape={spriteForId(selected.id)}
      className="tool-sprite--focus"
    />
  );
}

function infoLine(label: string, value: string) {
  return (
    <div className="marketplace__info-line">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function renderOtelBlock(otel: OtelView) {
  const signalSummary = otel.signals.length ? otel.signals.join(" + ") : "custom";
  const propagation = otel.propagation.length ? otel.propagation.join(", ") : "default";
  return (
    <div className="marketplace__inspector-block">
      <h3>Telemetry</h3>
      {infoLine("service", otel.service_name || "project scoped")}
      {infoLine("signals", signalSummary)}
      {infoLine("prop", propagation)}
      <div className="marketplace__tag-list">
        {otel.collector_sections.length ? otel.collector_sections.map((item) => <em key={item}>{item}</em>) : <em>sdk</em>}
      </div>
      {otel.live_tap?.enabled ? (
        <div className="marketplace__signal-list">
          <div className="marketplace__signal marketplace__signal--good">
            <strong>live tap</strong>
            <span>{`${otel.live_tap.transport || "local"} -> ${otel.live_tap.storage || "jsonl"} (${otel.live_tap.retention || "ephemeral"})`}</span>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export function ProjectToolsWorkbench({ payload }: { payload?: unknown }) {
  const view = (payload as ToolsPayload | undefined) ?? fallbackPayload;
  const [query, setQuery] = useState("");
  const [activeGroup, setActiveGroup] = useState("all");
  const selected = view.selected;

  const enabledCount = view.groups.flatMap((group) => group.entries).filter(projectEnabled).length;
  const readyCount = view.groups.flatMap((group) => group.entries).filter((item) => hasGlobalInstall(item) && !projectEnabled(item)).length;
  const entries = useMemo(() => visibleEntries(view.groups, activeGroup, query), [view.groups, activeGroup, query]);

  return (
    <main className="ui-shell tools-shell">
      <section className="marketplace">
        <ToolsWorkbenchHeader
          kicker="Project tools"
          title={view.project_name}
          subtitle={`Enable cached capabilities for this project.`}
          actions={(
            <div className="marketplace__plain-meta">
              <p>
                <strong>{enabledCount}</strong> enabled, <strong>{readyCount}</strong> ready to turn on.
              </p>
            </div>
          )}
        />

        <section className="marketplace__body">
          <aside className="marketplace__sidebar">
            <div className="marketplace__filters">
              <button
                type="button"
                className={activeGroup === "all" ? "is-active" : ""}
                onClick={() => setActiveGroup("all")}
              >
                <span>All</span>
                <em>{entries.length}</em>
              </button>
              {view.groups
                .filter((group) => group.entries.some((item) => hasGlobalInstall(item) || projectEnabled(item)))
                .map((group) => (
                  <button
                    key={group.label}
                    type="button"
                    className={activeGroup === group.label ? "is-active" : ""}
                    onClick={() => setActiveGroup(group.label)}
                  >
                    <span>{group.label}</span>
                    <em>{group.entries.filter((item) => hasGlobalInstall(item) || projectEnabled(item)).length}</em>
                  </button>
                ))}
            </div>

            <div className="marketplace__plain-status">
              <strong>{capabilitySummary(selected)}</strong>
              <p>{selected.locked ? "Managed baseline." : selected.enabled ? "Enabled here." : "Ready to enable."}</p>
            </div>
          </aside>

          <section className="marketplace__catalog">
            <div className="marketplace__catalog-head">
              <span>Project catalog</span>
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Filter packages"
                autoComplete="off"
              />
            </div>
            <div className="marketplace__list">
              {entries.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className={`marketplace__row ${item.selected ? "is-selected" : ""}`}
                  onClick={() => postIpc({ kind: "select-capability", data: item.id })}
                >
                  {renderToolMark(item)}
                  <div className="marketplace__row-copy">
                    <strong>{item.name}</strong>
                    <span>{item.summary}</span>
                  </div>
                  <div className="marketplace__row-meta">
                    <em>{item.group}</em>
                    <span>{projectEnabled(item) ? "enabled" : item.project_state === "locked" ? "locked" : "ready"}</span>
                  </div>
                </button>
              ))}
            </div>
          </section>

          <article className="marketplace__inspector">
            <div className="marketplace__inspector-hero">
              {renderSelectedMark(selected)}
              <div>
                <span>{selected.kind_label}</span>
                <h2>{selected.name}</h2>
                <p>{selected.summary}</p>
              </div>
            </div>

            <div className="marketplace__inspector-actions">
              {!selected.locked ? (
                <button type="button" onClick={() => postIpc({ kind: "toggle-project", data: selected.id })}>
                  {selected.enabled ? "disable" : "enable"}
                </button>
              ) : null}
              <button type="button" className="is-secondary" onClick={() => postIpc({ kind: "section-settings", data: selected.id })}>
                open cache
              </button>
            </div>

            <div className="marketplace__inspector-block">
              <h3>What this repo gets</h3>
              <div className="marketplace__tag-list">
                {selected.provides.length ? selected.provides.slice(0, 4).map((item) => <em key={item}>{item}</em>) : <span className="marketplace__muted">No declared outputs.</span>}
              </div>
            </div>

            {selected.otel ? renderOtelBlock(selected.otel) : null}

            <div className="marketplace__inspector-split">
              <div className="marketplace__inspector-block">
                <h3>TL;DR</h3>
                {infoLine("state", selected.locked ? "always on" : selected.enabled ? "enabled" : "ready to enable")}
                {infoLine("source", selected.source)}
                {infoLine("integration", selected.integration)}
              </div>
              <div className="marketplace__inspector-block">
                <h3>Checks</h3>
                <div className="marketplace__signal-list">
                  {selected.tester_checks.slice(0, 3).map((check) => (
                    <div key={`${check.label}-${check.detail}`} className={`marketplace__signal marketplace__signal--${check.tone}`}>
                      <strong>{check.label}</strong>
                      <span>{check.detail}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="marketplace__inspector-block">
              <h3>Needs</h3>
              {infoLine("env", String(selected.required_env.length))}
              {infoLine("binaries", String(selected.binaries.length))}
              {infoLine("endpoints", String(selected.endpoints.length))}
            </div>
          </article>
        </section>
      </section>
    </main>
  );
}
